import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { 
  Briefcase, MapPin, Clock, ChevronRight, Upload,
  Send, Loader2, CheckCircle, Building2, Users
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const JobCardSkeleton = () => (
  <div className="bg-white rounded-2xl p-6 border border-slate-100 animate-pulse">
    <div className="h-6 bg-slate-200 rounded w-3/4 mb-4" />
    <div className="h-4 bg-slate-200 rounded w-1/2 mb-4" />
    <div className="space-y-2">
      <div className="h-4 bg-slate-200 rounded w-full" />
      <div className="h-4 bg-slate-200 rounded w-5/6" />
    </div>
  </div>
);

const ApplicationForm = ({ job, onSuccess }) => {
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    full_name: '',
    work_email: '',
    phone: '',
    message: ''
  });
  const [resume, setResume] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      setResume(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!resume) {
      toast.error('Please upload your resume');
      return;
    }

    setLoading(true);
    try {
      const submitData = new FormData();
      submitData.append('job_id', job.id);
      submitData.append('full_name', formData.full_name);
      submitData.append('work_email', formData.work_email);
      submitData.append('phone', formData.phone);
      submitData.append('message', formData.message);
      submitData.append('resume', resume);

      await axios.post(`${API}/careers/apply`, submitData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Application submitted successfully!');
      onSuccess();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Application error:', error);
      toast.error('Failed to submit application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label className="text-text-primary">Full Name *</Label>
        <Input
          type="text"
          name="full_name"
          data-testid="career-name-input"
          value={formData.full_name}
          onChange={handleChange}
          required
          placeholder="John Doe"
          className="h-12"
        />
      </div>

      <div className="space-y-2">
        <Label className="text-text-primary">Work Email *</Label>
        <Input
          type="email"
          name="work_email"
          data-testid="career-email-input"
          value={formData.work_email}
          onChange={handleChange}
          required
          placeholder="john@example.com"
          className="h-12"
        />
      </div>

      <div className="space-y-2">
        <Label className="text-text-primary">Phone Number *</Label>
        <Input
          type="tel"
          name="phone"
          data-testid="career-phone-input"
          value={formData.phone}
          onChange={handleChange}
          required
          placeholder="+91 98765 43210"
          className="h-12"
        />
      </div>

      <div className="space-y-2">
        <Label className="text-text-primary">Resume *</Label>
        <div
          onClick={() => fileInputRef.current?.click()}
          className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center cursor-pointer hover:border-primary/50 transition-colors"
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.doc,.docx"
            onChange={handleFileChange}
            className="hidden"
          />
          {resume ? (
            <div className="flex items-center justify-center space-x-2 text-primary">
              <CheckCircle className="h-5 w-5" />
              <span>{resume.name}</span>
            </div>
          ) : (
            <>
              <Upload className="h-8 w-8 text-slate-400 mx-auto mb-2" />
              <p className="text-text-secondary text-sm">
                Click to upload (PDF, DOC, DOCX - Max 5MB)
              </p>
            </>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-text-primary">Message (Optional)</Label>
        <Textarea
          name="message"
          value={formData.message}
          onChange={handleChange}
          rows={4}
          placeholder="Tell us why you'd be a great fit..."
          className="resize-none"
        />
      </div>

      <Button
        type="submit"
        data-testid="career-submit-btn"
        disabled={loading}
        className="w-full bg-primary text-white hover:bg-primary-hover rounded-full h-12"
      >
        {loading ? (
          <>
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            Submitting...
          </>
        ) : (
          <>
            Submit Application
            <Send className="ml-2 h-5 w-5" />
          </>
        )}
      </Button>
    </form>
  );
};

const CareersPage = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const response = await axios.get(`${API}/jobs`);
        setJobs(response.data);
      } catch (error) {
        console.error('Failed to fetch jobs:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchJobs();
  }, []);

  const handleApply = (job) => {
    setSelectedJob(job);
    setDialogOpen(true);
  };

  return (
    <div data-testid="careers-page">
      {/* Hero */}
      <section className="bg-gradient-to-br from-warm-50 to-white py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="inline-block text-primary font-medium mb-4">Join Our Team</span>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl text-text-primary mb-6">
              Careers at Plutops
            </h1>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto">
              Join a team of passionate engineers building innovative solutions for global enterprises.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Why Join Us */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Building2,
                title: 'Work on Impactful Projects',
                description: 'Build solutions for Fortune 500 companies and innovative startups alike.'
              },
              {
                icon: Users,
                title: 'Collaborative Culture',
                description: 'Work with talented professionals in a supportive, growth-oriented environment.'
              },
              {
                icon: Briefcase,
                title: 'Growth Opportunities',
                description: 'Continuous learning, certifications, and clear career progression paths.'
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-slate-50 rounded-2xl p-8 text-center"
              >
                <div className="h-14 w-14 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <item.icon className="h-7 w-7 text-primary" />
                </div>
                <h3 className="font-heading font-bold text-lg text-text-primary mb-2">
                  {item.title}
                </h3>
                <p className="text-text-secondary text-sm">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Current Openings */}
      <section className="py-24 bg-slate-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading font-bold text-3xl text-text-primary mb-4">
              Current Openings
            </h2>
            <p className="text-text-secondary">
              Find your next opportunity with us
            </p>
          </motion.div>

          <div className="space-y-6">
            {loading ? (
              Array.from({ length: 3 }).map((_, i) => <JobCardSkeleton key={i} />)
            ) : jobs.length === 0 ? (
              <div className="text-center py-12">
                <Briefcase className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                <p className="text-text-secondary">No open positions at the moment. Check back soon!</p>
              </div>
            ) : (
              jobs.map((job, index) => (
                <motion.div
                  key={job.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-slate-100 hover:shadow-md transition-shadow"
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                    <div className="flex-1">
                      <h3 className="font-heading font-bold text-xl text-text-primary mb-3">
                        {job.title}
                      </h3>
                      
                      <div className="flex flex-wrap gap-4 mb-4">
                        <span className="inline-flex items-center text-sm text-text-muted">
                          <MapPin className="h-4 w-4 mr-1 text-primary" />
                          {job.location}
                        </span>
                        <span className="inline-flex items-center text-sm text-text-muted">
                          <Briefcase className="h-4 w-4 mr-1 text-primary" />
                          {job.job_type}
                        </span>
                        <span className="inline-flex items-center text-sm text-text-muted">
                          <Clock className="h-4 w-4 mr-1 text-primary" />
                          {job.experience_level}
                        </span>
                      </div>
                      
                      <p className="text-text-secondary mb-4">
                        {job.description}
                      </p>
                      
                      {job.salary_range && (
                        <p className="text-sm text-primary font-medium">
                          {job.salary_range}
                        </p>
                      )}
                    </div>
                    
                    <div>
                      <Button
                        onClick={() => handleApply(job)}
                        data-testid={`apply-btn-${job.slug}`}
                        className="bg-primary text-white hover:bg-primary-hover rounded-full px-8"
                      >
                        Apply Now
                        <ChevronRight className="ml-1 h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Application Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              Apply for {selectedJob?.title}
            </DialogTitle>
          </DialogHeader>
          {selectedJob && (
            <ApplicationForm
              job={selectedJob}
              onSuccess={() => setDialogOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CareersPage;
